import React, { useState } from 'react';
import {
    Button,
    Card,
    Image,
    Modal,
    message,
    List,
    Divider,
    Row,
    Col,
} from 'antd';
import { css } from 'emotion';
import {
    DeleteOutlined,
    ExclamationCircleOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import {
    Upload,
    UploadFileInfo,
    UploadOnAddEvent,
    UploadOnRemoveEvent,
} from '@progress/kendo-react-upload';

import { LocalizationProvider } from '@progress/kendo-react-intl';
import {
    useGetDocumentUploadListByReferenceParams,
    useGetUserData,
} from 'waypoint-hooks';
import { deleteDocument, uploadDocument } from 'waypoint-requests';
import { DocumentUpload } from 'waypoint-types';
import { isValidFileSize } from 'waypoint-utils';
import theme from 'config/theme';

const { confirm } = Modal;

const pageContainer = css`
    background-color: #ededed;
    margin: 0;
    padding: 22px 20px 20px 16px;
    overflow: visible;
    position: relative;
    min-height: 85%;
`;
const header = css`
    font-size: 20px;
    font-weight: bold;
    margin: 0;
    color: #5e5e5e;
    line-height: 1.5;
`;

const hideKendoUploadElementsClass = css`
    .k-actions,
    .k-progressbar,
    .k-dropzone-hint {
        display: none;
    }
`;

const helperText = css`
    font-size: 12px;
    color: #5e5e5e;
    margin-top: 4px;
    text-align: right;
`;

const MAX_FILE_SIZE_MB = 5;

const MAX_FILES_ALLOWED = 5;

const LOGO_UPLOAD_RESTRICTIONS = {
    allowedExtensions: ['.png', '.jpg'],
    maxFileSize: MAX_FILE_SIZE_MB * 1024 * 1024,
};

const CompanyLogo = () => {
    const { clientId, isAdmin } = useGetUserData();
    const [files, setFiles] = useState<UploadFileInfo[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const {
        data: companyLogos,
        isLoading: isLoadingCompanyLogos,
        mutate,
    } = useGetDocumentUploadListByReferenceParams(
        'company_logo',
        `${clientId}`
    );

    const onRemove = (event: UploadOnRemoveEvent) => {
        if (!event.affectedFiles || !event.newState) {
            return;
        }
        setFiles(event.newState);
    };

    const onAddFile = (event: UploadOnAddEvent) => {
        if (!event.newState) {
            return;
        }

        const totalFiles = (companyLogos?.length || 0) + event.newState.length;

        if (totalFiles > MAX_FILES_ALLOWED) {
            message.error(
                `You can only upload up to ${MAX_FILES_ALLOWED} logos`
            );
            event.newState = event.newState.slice(
                0,
                MAX_FILES_ALLOWED - (companyLogos?.length || 0)
            );
        }

        setFiles(event.newState);
    };

    const deleteLogo = async (id: string) => {
        confirm({
            title: 'Are you sure you want to delete this logo?',
            icon: <ExclamationCircleOutlined />,
            content: 'This action cannot be undone.',
            centered: true,
            onOk() {
                mutate(
                    async () => {
                        await deleteDocument(id);
                        message.success('Logo deleted successfully.');
                        return companyLogos?.filter(
                            (item: DocumentUpload) => item.id !== id
                        );
                    },
                    {
                        optimisticData: companyLogos,
                        rollbackOnError: true,
                        populateCache: false,
                        revalidate: true,
                    }
                );
            },
        });
    };

    const onUploadFile = async () => {
        if (!files || !files.length) {
            return;
        }

        setIsUploading(true);

        for (const file of files) {
            if (file.getRawFile) {
                if (!isValidFileSize(MAX_FILE_SIZE_MB, file?.getRawFile())) {
                    message.error(
                        `File size should not exceed ${MAX_FILE_SIZE_MB} MB`
                    );
                    continue;
                }

                const documentToUpload = new FormData();

                documentToUpload.append('upload', file?.getRawFile());

                await mutate(
                    async () => {
                        try {
                            await uploadDocument(
                                documentToUpload,
                                'company_logo',
                                `${clientId}`
                            );

                            message.success('Document uploaded successfully');
                            return companyLogos;
                        } catch (e) {
                            setIsUploading(false);
                            message.error('Error uploading document');
                        }
                    },
                    {
                        rollbackOnError: true,
                        populateCache: true,
                        revalidate: true,
                    }
                );
            }
        }

        setIsUploading(false);
        setFiles([]);
    };

    return (
        <div className={pageContainer}>
            <Card
                title={<h1 className={header}>Company Logo</h1>}
                loading={isLoadingCompanyLogos}
                extra={<></>}
            >
                <Row gutter={24}>
                    <Col span={8}>
                        <div>
                            <LocalizationProvider language="custom">
                                <Upload
                                    className={hideKendoUploadElementsClass}
                                    batch={false}
                                    multiple={true}
                                    restrictions={LOGO_UPLOAD_RESTRICTIONS}
                                    files={files}
                                    accept="image/png, image/jpg"
                                    withCredentials={false}
                                    autoUpload={false}
                                    onAdd={onAddFile}
                                    onRemove={onRemove}
                                    disabled={
                                        companyLogos?.length ===
                                            MAX_FILES_ALLOWED ||
                                        isUploading ||
                                        !isAdmin
                                    }
                                />
                                <p className={helperText}>
                                    {companyLogos?.length === MAX_FILES_ALLOWED
                                        ? 'Maximum files uploaded'
                                        : `Max ${MAX_FILES_ALLOWED} images allowed, ${MAX_FILE_SIZE_MB}MB max size, PNG or JPG only`}
                                </p>
                            </LocalizationProvider>
                            {files?.length && isAdmin ? (
                                <Button
                                    loading={isUploading}
                                    disabled={
                                        isUploading ||
                                        companyLogos?.length ===
                                            MAX_FILES_ALLOWED
                                    }
                                    type="primary"
                                    icon={<UploadOutlined />}
                                    style={{ marginTop: 6 }}
                                    onClick={() => onUploadFile()}
                                >
                                    Upload new logo
                                </Button>
                            ) : null}
                        </div>
                    </Col>
                    <Col span={16}>
                        <div>
                            <h2 style={{ marginTop: 0 }}>Available Logos</h2>
                            <Divider
                                style={{
                                    borderBlockStartColor: theme.colors.black,
                                }}
                            />
                            <List
                                dataSource={companyLogos}
                                locale={{ emptyText: 'No logos uploaded' }}
                                renderItem={(item) => (
                                    <List.Item key={item.id}>
                                        <List.Item.Meta
                                            avatar={
                                                <Image
                                                    src={item.document_url}
                                                    width={75}
                                                />
                                            }
                                        />
                                        <div>
                                            {isAdmin ? (
                                                <Button
                                                    onClick={() =>
                                                        deleteLogo(item.id)
                                                    }
                                                    type="ghost"
                                                >
                                                    <DeleteOutlined />
                                                </Button>
                                            ) : null}
                                        </div>
                                    </List.Item>
                                )}
                            />
                            <Divider
                                style={{
                                    borderBlockStartColor: theme.colors.black,
                                }}
                            />
                        </div>
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default CompanyLogo;
